import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQrcode } from "@fortawesome/pro-regular-svg-icons";
import Input from "components/form/input/Input";
import "./kioskHome.scss";
import { KioskHelpFooter } from "./KioskHelpFooter";
import { useHistory } from "react-router-dom";
import { useAppDispatch } from "hooks/redux";
import { SwitchUser, UserLogout } from "api/rpc";
import { logoutFacility } from "redux/actions/facility";
import { userLogout } from "redux/actions/auth";
import { StatusCode } from "api/protocols";
import KioskLogoutModal from "./KioskLogoutModal";
import { isPositiveInteger } from "helpers/Helpers";
import { showError } from "redux/actions/ui";

interface IKioskHomeProps {
  bookingReference: string;
  setBookingReference: (val: string) => void;
  onFindBooking: () => void;
  onScanQR: () => void;
}

export function KioskHome({ bookingReference, setBookingReference, onFindBooking, onScanQR }: IKioskHomeProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const [displaySwitchUserModal, setDisplaySwitchUserModal] = useState(false);
  const [userCode, setUserCode] = useState("");

  const handleOpenLogoutModal = () => {
    setDisplaySwitchUserModal(true);
    setUserCode("");
  };

  const handleCloseLogoutModal = () => {
    setDisplaySwitchUserModal(false);
    setUserCode("");
  };

  const handleLogoutWithCode = async () => {
    if (isPositiveInteger(userCode)) {
      const switchUserResponse = await SwitchUser({ quick_code: parseInt(userCode) }, true);

      if (switchUserResponse.status !== StatusCode.OK) {
        dispatch(showError(t("Invalid PIN")));
        return;
      }

      // PIN is valid, perform full logout
      await UserLogout(true);
      void dispatch(logoutFacility());
      void dispatch(userLogout());
      localStorage.setItem("switch_user_after_transaction", "false");

      handleCloseLogoutModal();
      history.push("/");
    }
  };

  const handleUserCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserCode(event.target.value);
  };

  const handleUserCodeKeyDown = (event: any) => {
    if (event.code === "Enter" || event.code === "NumpadEnter") {
      void handleLogoutWithCode();
    }
  };

  useEffect(() => {
    const focusInput = () => {
      if (!displaySwitchUserModal) {
        document.getElementById("kiosk-booking-input")?.focus();
      }
    };

    // Initial focus and listener for clicks anywhere else
    focusInput();
    window.addEventListener("click", focusInput);

    // Backup interval to ensure focus is maintained
    const interval = setInterval(focusInput, 500);

    return () => {
      window.removeEventListener("click", focusInput);
      clearInterval(interval);
    };
  }, [displaySwitchUserModal]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === "Enter" || event.code === "NumpadEnter") {
      if (bookingReference.trim().length > 0) {
        onFindBooking();
      }
    }
  };

  return (
    <div className="ui-kiosk-container">
      <Button onClick={handleOpenLogoutModal} type="secondary" className="ui-kiosk-logout-btn">
        {t("Logout")}
      </Button>
      <img src="../../../../public/images/Tee-On.png" alt="teeon.com" className="ui-kiosk-logo" />

      <p className="ui-kiosk-title">{t("Welcome to self check-in")}</p>
      <p className="ui-kiosk-subtitle">
        {t("Please enter your booking reference or scan your code to start self check-in.")}
      </p>

      <div className="ui-kiosk-input-container">
        <Input
          id="kiosk-booking-input"
          placeholder={t("Enter confirmation #, membership ID, etc.")}
          value={bookingReference}
          onChange={e => setBookingReference(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      </div>

      <div className="ui-kiosk-button-container">
        <Button
          onClick={onFindBooking}
          type="primary"
          className="ui-kiosk-button"
          disabled={bookingReference.length === 0}
        >
          {t("Find booking")}
        </Button>

        <Button onClick={onScanQR} type="secondary" className="ui-kiosk-button ml-0">
          <FontAwesomeIcon icon={faQrcode} /> <span className="ml-2">{t("Scan QR code")}</span>
        </Button>

        <KioskHelpFooter />
      </div>

      <KioskLogoutModal
        open={displaySwitchUserModal}
        onCancel={handleCloseLogoutModal}
        onOk={handleLogoutWithCode}
        okDisabled={!isPositiveInteger(userCode)}
        userCode={userCode}
        onChange={handleUserCodeChange}
        onKeyDown={handleUserCodeKeyDown}
        autoFocus
      />
    </div>
  );
}
